import {
  fetchFeaturedPublications,
  fetchPinnedPublications,
  fetchPopularPublications,
  fetchRecentGroupDiscussions,
  fetchRecentPublications,
} from '@/api/publication';
import {
  fetchColleaguesOfColleagues,
  getCurrentUserSuggestions,
} from '@/api/user';
import CommunityPicto from '@/components/presentational/img/icons/CommunityPicto';
import BgMasqueHaut from '@/components/presentational/img/v2024/BgMasqueHaut.svg';
import VolDeMouettesCommunautes from '@/components/presentational/img/v2024/VolDeMouettesCommunautes.svg';
import { Discussion } from '@/types/core/Discussion';
import { Group } from '@/types/core/Group';
import { Publication } from '@/types/core/Publication';
import { User } from '@/types/core/User';
import { SearchResult } from '@/types/search/searchresult';
import { NextPageContextWithRedux } from '@/types/store/NextPageContextWithRedux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import withUser from '@/withUser';
import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Image,
  Link,
  Select,
  Show,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Breadcrumb, { BreadcrumbItem } from '@components/app/Breadcrumb';
import HidableSearchBar from '@components/app/HidableSearchBar';
import PublicationCard from '@components/app/PublicationCard';
import SearchBar from '@components/app/SearchBar';
import MobilePublicationTypeFilterModal from '@components/community/MobilePublicationTypeFilterModal';
import PublishNewsModal from '@components/community/PublishNewsModal';
import PublishPedagogicalResourceModal from '@components/community/resource/PublishPedagogicalResourceModal';
import { SelectOnMobilePublishModal } from '@components/community/SelectOnMobilePublishModal';
import Header, { NavCategory, NavSubCategory } from '@components/Header';
import LoginModal from '@components/LoginModal';
import ActionProposalCard, {
  ActionProposalCardLink,
  ActionProposalCardText,
} from '@components/presentational/Cards/ActionProposalCard';
import CardBody from '@components/presentational/Cards/Parts/CardBody';
import CardFooter from '@components/presentational/Cards/Parts/CardFooter';
import CardTitle from '@components/presentational/Cards/Parts/CardTitle';
import SimpleCard from '@components/presentational/Cards/SimpleCard';
import Chapo from '@components/presentational/Chapo';
import MemberPreview from '@components/presentational/MemberPreview';
import MobileBottomNav, {
  mobileBottomNavStyle,
} from '@components/presentational/MobileBottomNav';
import RessourcePreview from '@components/presentational/RessourcePreview';
import UIWidth from '@components/presentational/UIWidth';
import FrontLayout from '@layouts/FrontLayout';
import Head from 'next/head';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  PiArrowRightThin,
  PiCaretRight,
  PiFoldersThin,
  PiNotePencilThin,
  PiPlusThin,
  PiShuffleThin,
} from 'react-icons/pi';
import { useSelector } from 'react-redux';
import { useGoogleTagManager } from '@components/app/hooks/useGTM';

const FILTER_ALL = 'all';
const FILTER_JOB = 'job';
const FILTER_CULTURAL_EVENT = 'cultural_event';
const FILTER_COURSE = 'course';
const FILTER_WEBINAR = 'webinar';
const FILTER_CONTEST = 'contest';
const FILTER_MUTUAL_ASSISTANCE = 'mutual_assistance';
const FILTER_COLLOQUIUM = 'colloquium';
const FILTER_SCHOOL_EXCHANGE = 'school_exchange';

const ORDER_BY_RELEVANCE = 'relevance';
const ORDER_BY_POPULARITY = 'popularity';
const ORDER_BY_RECENT = 'recent';

const FILTERS = [
  { value: FILTER_ALL, label: 'Voir tout' },
  { value: FILTER_JOB, label: "Offre d'emploi" },
  { value: FILTER_CULTURAL_EVENT, label: 'Événement culturel' },
  { value: FILTER_COURSE, label: 'Formation' },
  { value: FILTER_WEBINAR, label: 'Webinaire' },
  { value: FILTER_CONTEST, label: 'Concours' },
  { value: FILTER_MUTUAL_ASSISTANCE, label: 'Entraide' },
  { value: FILTER_COLLOQUIUM, label: 'Colloque' },
  { value: FILTER_SCHOOL_EXCHANGE, label: 'Echange scolaire' },
];
const ORDERS = [
  { value: ORDER_BY_RECENT, label: 'Du + récent au + ancien' },
  // { value: ORDER_BY_RELEVANCE, label: 'Pertinence' },
  { value: ORDER_BY_POPULARITY, label: 'Popularité' },
];

const ITEMS_PER_LOAD_MORE = 5;

function MainPublicationsList({
  filter,
  setFilter,
  orders,
  order,
  setOrder,
  search,
  pinned,
  setPinned,
  suggestedPublications,
  popularPublications,
  featuredPublications,
  refreshFeatured,
  colleaguesOfColleagues,
  recentGroupDiscussions,
  filterNews,
  onPublishModalOpen,
  onPublishPedagogicalModalOpen,
  onClickLogin,
}: {
  filter: string;
  setFilter: Function;
  orders: { label: string; value: string }[];
  order: string;
  setOrder: Function;
  search?: string;
  pinned: Publication[];
  setPinned: Function;
  suggestedPublications: Publication[];
  popularPublications: Publication[];
  featuredPublications: Publication[];
  refreshFeatured: () => void;
  colleaguesOfColleagues: SearchResult<User>;
  recentGroupDiscussions: SearchResult<Publication>;
  filterNews: boolean;
  onPublishModalOpen;
  onPublishPedagogicalModalOpen;
  onClickLogin;
}) {
  const { replace, query } = useRouter();

  const currentUser = useSelector(
    (state: ReduxRootState) => state.authentication.user
  );

  const [loadingMore, setLoadingMore] = useState(false);
  const [showLoadMore, setShowLoadMore] = useState(true);

  // cached and lazy loaded publications list
  // by popularity
  const [populars, setPopulars] = useState(popularPublications);
  const [popularsPage, setPopularsPage] = useState(1);
  // by publications date
  const [recentsPage, setRecentsPage] = useState(0);
  const [recents, setRecents] = useState([]);

  // list of publications suggested for the logged user
  const [suggested, setSuggested] = useState(suggestedPublications);
  const filteredSuggested = useMemo(() => {
    return suggested.filter(publication => {
      return (
        (!filter ||
          filter === 'all' ||
          publication.newsTypes?.includes(filter)) &&
        (!search || publication.title.includes(search)) &&
        !publication.pinned
      );
    });
  }, [suggested, filter, search]);
  // relevance order has no meaning for unlogged visitor and depends on logged user
  // useEffect(() => {
  //   async function loadSuggested() {
  //     const currentUserSuggestions = await getCurrentUserSuggestions(
  //       true,
  //       false,
  //       false
  //     );
  //     setSuggested(
  //       currentUserSuggestions.filter(
  //         publication => !filterNews || publication.__TYPE__ === 'news'
  //       )
  //     );
  //     // we order by relevance after login only if logged user has a suggestion list.
  //     setOrder(currentUserSuggestions.length > 0 ? ORDER_BY_RELEVANCE : order);
  //   }
  //
  //   loadSuggested();
  // }, [currentUser]);

  // we don't have control on pinned/suggested publications lists
  // so we truncate the list to display to this length
  const [publicationListLength, setPublicationListLength] = useState(
    2 * ITEMS_PER_LOAD_MORE
  );

  // the displayed main publications list
  const publications = useMemo(() => {
    // publication list always begins with pinned publications
    let publicationList = pinned;
    // relevance order displays first the user's suggestions then other publications by popularity
    if (order === ORDER_BY_RELEVANCE) {
      publicationList = [...publicationList, ...filteredSuggested, ...populars];
    }
    if (order === ORDER_BY_POPULARITY) {
      publicationList = [...publicationList, ...populars];
    }
    if (order === ORDER_BY_RECENT) {
      publicationList = [...publicationList, ...recents];
    }
    return publicationList.slice(0, publicationListLength);
  }, [
    pinned,
    filteredSuggested,
    populars,
    recents,
    publicationListLength,
    order,
    filter,
  ]);
  const currentFilters = useMemo(() => {
    let filters: Record<string, string> = {
      types: filterNews ? 'news' : 'pedagogicalResource,news',
    };

    // If we are in the news section, we filter only by the user's country
    if (filterNews) {
      filters = {
        ...filters,
        country: currentUser?.country?.id ?? '',
      };
    } else {
      filters = {
        ...filters,
        worldOrCountry: currentUser?.country?.id ?? '',
      };
    }

    if (filter !== FILTER_ALL) {
      filters = {
        ...filters,
        newsType: filter,
      };
    }
    if (search) {
      filters = {
        ...filters,
        textQuery: search,
      };
    }

    return filters;
  }, [filterNews, currentUser, filter, search]);

  async function refreshList() {
    try {
      const pinnedPublicationsPromise = fetchPinnedPublications({
        worldOrCountry: currentUser?.country?.id ?? '',
      });
      const suggestionsPromise = currentUser
        ? getCurrentUserSuggestions(true, false, false)
        : Promise.resolve([]);

      const [
        pinnedPublications,
        suggestions,
      ] = await Promise.all([
        pinnedPublicationsPromise,
        suggestionsPromise,
      ]);
      setPinned(pinnedPublications.items);
      setSuggested(suggestions);

      if (order === ORDER_BY_RECENT) {
        setRecentsPage(2);
        const recentPublications = await fetchRecentPublications(
          2 * ITEMS_PER_LOAD_MORE,
          1,
          currentFilters
        );
        setRecents(recentPublications.items);
        setPopularsPage(0);
        setPopulars([]);
      } else if (
        order === ORDER_BY_RELEVANCE ||
        order === ORDER_BY_POPULARITY
      ) {
        setPopularsPage(2);
        const popularPublications = await fetchPopularPublications(
          2 * ITEMS_PER_LOAD_MORE,
          1,
          currentFilters
        );
        setPopulars(popularPublications.items);
        setRecentsPage(0);
        setRecents([]);
      }
    } catch (error) {
      console.error('Failed to refresh the list', error);
    }
  }

  useEffect(() => {
    async function refreshList() {
      // changing filter value resets recents and populars lists
      if (order === ORDER_BY_RECENT) {
        setRecentsPage(2);
        const recents = await fetchRecentPublications(
          2 * ITEMS_PER_LOAD_MORE,
          1,
          currentFilters
        ).then(result => result.items);

        setRecents(recents);
        // TODO : better would be to keep memory of the list's filter and reset only on order change if the filter value has changed
        setPopularsPage(0);
        setPopulars([]);
      }
      if (order === ORDER_BY_RELEVANCE || order === ORDER_BY_POPULARITY) {
        setPopularsPage(2);
        setPopulars(
          await fetchPopularPublications(
            2 * ITEMS_PER_LOAD_MORE,
            1,
            currentFilters
          ).then(result => result.items)
        );
        setRecentsPage(0);
        setRecents([]);
      }
      setPublicationListLength(2 * ITEMS_PER_LOAD_MORE);
      setShowLoadMore(true);
    }
    refreshList();
  }, [currentFilters]);

  // We get the groups of the recent discussions
  const recentsGroups = useMemo<Group[]>(() => {
    const groups: Group[] = [];
    recentGroupDiscussions.items.forEach((publication: Discussion) => {
      if (!groups.find(group => group?.id === publication.group?.id)) {
        groups.push(publication.group);
      }
    });
    return groups;
  }, [recentGroupDiscussions]);

  return (
    <Flex
      direction={['column', null, 'row']}
      justify="space-between"
      align="stretch"
      gap={['3rem', '', '1rem']}
    >
      <Flex
        direction="column"
        gap="2rem"
        width={['100%', '', 'calc(100% - 19rem)']}
        height="fit-content"
      >
        {currentUser && (
          <Show breakpoint="(min-width: 768px)">
            <ActionProposalCard width="100%" accentColor="tournesol">
              <Flex
                direction={['column', 'row']}
                gap={['0.5rem', '1rem', '1.25rem']}
                align="center"
                w="100%"
              >
                <Box
                  minWidth={['4rem', '', '5rem']}
                  height={['4rem', '', '5rem']}
                  mb={['0.5rem', '0']}
                  mr={{ md: '0.75rem' }}
                >
                  <Avatar
                    src={`/data/avatars/${currentUser?.avatar?.filename}`}
                    name={currentUser?.displayName}
                    width={'100%'}
                    height={'100%'}
                    aspectRatio={1}
                  />
                </Box>
                <ActionProposalCardText textCantWrap>
                  Publier une
                </ActionProposalCardText>
                <Button
                  variant="sunflower"
                  width="100%"
                  size={['sm', '', 'md']}
                  onClick={onPublishModalOpen}
                >
                  <Icon as={PiNotePencilThin} boxSize="2rem" mr="0.625rem" />
                  information
                </Button>
                <ActionProposalCardText>ou</ActionProposalCardText>
                <Button
                  variant="sunflower"
                  width="100%"
                  size={['sm', '', 'md']}
                  onClick={() => {
                    onPublishPedagogicalModalOpen();
                  }}
                >
                  <Icon as={PiFoldersThin} boxSize="2rem" mr="0.625rem" />
                  ressource
                </Button>
              </Flex>
            </ActionProposalCard>
          </Show>
        )}
        {!currentUser && (
          <ActionProposalCard width="100%" accentColor="tournesol">
            <Flex direction="column" gap="1rem" align="center">
              <ActionProposalCardText>
                Améliorez votre pratique professionnelle grâce à votre
                communauté éducative
              </ActionProposalCardText>
              <Flex align={['center', 'start']} gap={['0.5rem', '1rem']}>
                <ActionProposalCardLink
                  href="/s-inscrire"
                  buttonVariants="sunflowers"
                >
                  S'inscrire
                </ActionProposalCardLink>
                <Button
                  variant="sunflower"
                  size={['sm', '', 'md']}
                  onClick={onClickLogin}
                >
                  Connexion
                </Button>
              </Flex>
            </Flex>
          </ActionProposalCard>
        )}
        <Show breakpoint="(max-width: 768px)">
          <SearchBar placeholder="Rechercher une publication" size="sm" />
        </Show>
        <Flex
          display={['none', 'flex']}
          direction={['column', 'row']}
          gap={['0.5rem', '', '0']}
          justify="space-between"
          mt="2rem"
        >
          <Flex justify={['space-between', null]} gap="0.5rem" align="center">
            <Text whiteSpace="nowrap">Filtrer:</Text>
            <Select
              width="fit-content"
              value={filter}
              onChange={async e => {
                setFilter(e.target.value);
              }}
            >
              {FILTERS.map(filter => {
                return <option value={filter.value}>{filter.label}</option>;
              })}
            </Select>
          </Flex>
          <Flex justify={['space-between', null]} gap="0.5rem" align="center">
            <Text whiteSpace="nowrap">Trier par :</Text>
            <Select
              width="fit-content"
              value={order}
              onChange={async e => {
                setOrder(e.target.value);
                // if recent/popular list is empty, we fetch the 2 first pages to init his content.
                if (
                  e.target.value === ORDER_BY_RECENT &&
                  recents.length === 0
                ) {
                  setRecentsPage(2);
                  setRecents(
                    await fetchRecentPublications(
                      2 * ITEMS_PER_LOAD_MORE,
                      1,
                      currentFilters
                    ).then(result => result.items)
                  );
                }
                if (
                  (e.target.value === ORDER_BY_RELEVANCE ||
                    e.target.value === ORDER_BY_POPULARITY) &&
                  populars.length === 0
                ) {
                  setPopularsPage(2);
                  setPopulars(
                    await fetchPopularPublications(
                      2 * ITEMS_PER_LOAD_MORE,
                      1,
                      currentFilters
                    ).then(result => result.items)
                  );
                }

                setPublicationListLength(2 * ITEMS_PER_LOAD_MORE);
                setShowLoadMore(true);
              }}
            >
              {orders.map(order => {
                return <option value={order.value}>{order.label}</option>;
              })}
            </Select>
          </Flex>
        </Flex>
        {publications.map(publication => {
          return (
            <PublicationCard
              key={publication.id}
              publication={publication}
              onEndPublicationRemove={refreshList}
              onEndPublicationPin={refreshList}
              onEndPublicationFeature={refreshFeatured}
            />
          );
        })}

        {showLoadMore && (
          <Center>
            <Button
              size="sm"
              isDisabled={loadingMore}
              onClick={async () => {
                setLoadingMore(true);
                // we fetch the next page only if recents/populars list doesn't contain enough items.
                // these lists are cached as long as we don't change filter value.
                if (
                  order === ORDER_BY_RECENT &&
                  pinned.length + recents.length <
                    publicationListLength + ITEMS_PER_LOAD_MORE
                ) {
                  const result = await fetchRecentPublications(
                    ITEMS_PER_LOAD_MORE,
                    recentsPage + 1,
                    currentFilters
                  );
                  if (result.items.length < ITEMS_PER_LOAD_MORE) {
                    setShowLoadMore(false);
                  }
                  setRecents([...recents, ...result.items]);
                  setRecentsPage(recentsPage + 1);
                }
                if (
                  (order === ORDER_BY_RELEVANCE &&
                    pinned.length +
                      suggestedPublications.length +
                      populars.length <
                      publicationListLength + ITEMS_PER_LOAD_MORE) ||
                  (order === ORDER_BY_POPULARITY &&
                    pinned.length + populars.length <
                      publicationListLength + ITEMS_PER_LOAD_MORE)
                ) {
                  const result = await fetchPopularPublications(
                    ITEMS_PER_LOAD_MORE,
                    popularsPage + 1,
                    currentFilters
                  );
                  if (result.items.length < ITEMS_PER_LOAD_MORE) {
                    setShowLoadMore(false);
                  }
                  setPopulars([...populars, ...result.items]);
                  setPopularsPage(popularsPage + 1);
                }
                setPublicationListLength(
                  publicationListLength + ITEMS_PER_LOAD_MORE
                );
                setLoadingMore(false);
              }}
            >
              <Icon as={PiPlusThin} boxSize={7} mr="0.625rem" />
              Voir plus de publications
            </Button>
          </Center>
        )}
      </Flex>

      <Flex
        width={['100%', '', '19rem']}
        direction={{ md: 'column' }}
        justify={{ sm: 'center', md: 'revert' }}
        gap={['2rem', 'calc(1rem - 0.625rem) 1rem', '2rem']}
        wrap="wrap"
      >
        {featuredPublications.length > 0 && (
          <SimpleCard
            accentColor="tournesol.100"
            width="100%"
            flexGrow={0}
            height="revert"
          >
            <CardTitle
              title="Publications à la Une"
              subtitle="L'actualité à ne pas manquer"
              padding="0.25rem 0"
            />
            <CardBody>
              <Flex direction="column" gap={['1rem', '1.25rem']}>
                {featuredPublications.map(publication => {
                  return (
                    <RessourcePreview
                      key={publication.id}
                      imageSrc={
                        publication.image?.filename
                          ? `/data/event/images/${publication.image.filename}`
                          : ''
                      }
                      imageAlt={publication.imageAlt}
                      ressourceLink="#"
                      publication={publication}
                    >
                      {publication.title}
                    </RessourcePreview>
                  );
                })}
              </Flex>
            </CardBody>
          </SimpleCard>
        )}

        {colleaguesOfColleagues.items.length > 0 && (
          <SimpleCard
            accentColor="tournesol.100"
            width={['100%', 'calc(50% - 0.5rem)', '100%']}
            flexGrow={0}
            height="revert"
          >
            <CardTitle title="Les membres de mon pays" padding="0.25rem 0" />
            <CardBody>
              <Flex direction="column" gap={['1rem', '1.25rem']}>
                {colleaguesOfColleagues.items.map(member => {
                  return <MemberPreview key={member.id} user={member} />;
                })}
              </Flex>
            </CardBody>
            <CardFooter>
              <Flex align="center" gap="0.625rem">
                <Icon as={PiArrowRightThin} boxSize="1.375rem" />
                <NextLink passHref href="/communautes/membres">
                  <Link textDecoration="underline">
                    Découvrir d'autres membres
                  </Link>
                </NextLink>
              </Flex>
            </CardFooter>
          </SimpleCard>
        )}

        {recentsGroups.length > 0 && (
          <SimpleCard
            accentColor="tournesol.100"
            width={['100%', 'calc(50% - 0.5rem)', '100%']}
            flexGrow={0}
            height="revert"
          >
            <CardTitle
              title="Mes groupes"
              subtitle="Les discussions récentes"
              padding="0.25rem 0"
            />
            <CardBody>
              <Flex direction="column" gap={['1rem', '1.25rem']}>
                {recentsGroups.map(group => {
                  return (
                    <NextLink
                      as={`/groupe/${group.slug}/${group.id}/discussions`}
                      href={{
                        pathname: '/groupe/discussions',
                        query: {
                          slug: group.slug,
                          group: group.id,
                        },
                      }}
                      passHref
                    >
                      <Link
                        key={group.id}
                        display="flex"
                        alignItems="center"
                        gap={['1rem', '1.25rem']}
                      >
                        <Flex align="center" gap="0.625rem">
                          <Icon as={PiCaretRight} boxSize="1.375rem" />
                          <Text> {group.name}</Text>
                        </Flex>
                      </Link>
                    </NextLink>
                  );
                })}
              </Flex>
            </CardBody>
            <CardFooter>
              <Flex align="center" gap="0.625rem">
                <Icon as={PiArrowRightThin} boxSize="1.375rem" />
                <NextLink
                  href={'/communautes/groupes'}
                  as={'/communautes/groupes'}
                  passHref
                >
                  <Link textDecoration="underline">
                    Voir toutes les discussions
                  </Link>
                </NextLink>
              </Flex>
            </CardFooter>
          </SimpleCard>
        )}
      </Flex>
    </Flex>
  );
}

function Communities({
  activeTab,
  pinnedPublications,
  suggestedPublications,
  popularPublications,
  featuredPublications,
  colleaguesOfColleagues,
  recentGroupDiscussions,
  searchInit,
}: {
  activeTab: number;
  pinnedPublications: SearchResult<Publication>;
  suggestedPublications: Publication[];
  popularPublications: SearchResult<Publication>;
  featuredPublications: SearchResult<Publication>;
  colleaguesOfColleagues: SearchResult<User>;
  recentGroupDiscussions: SearchResult<Publication>;
  searchInit?: string;
}) {
  const currentUser = useSelector(
    (state: ReduxRootState) => state.authentication.user
  );

  const [filter, setFilter] = useState(FILTER_ALL);
  const [order, setOrder] = useState(ORDER_BY_RECENT);
  const [search, setSearch] = useState(searchInit);

  const allowedOrders = ORDERS.filter(
    order => currentUser || order.value === ORDER_BY_RECENT
  );

  const toast = useToast();

  const [pinned, setPinned] = useState(pinnedPublications.items);
  const pinnedNews = useMemo(() => {
    return pinned.filter(
      publication => publication.__TYPE__ === 'news' && publication.country
    );
  }, [pinned]);
  const suggestedNews = useMemo(() => {
    return suggestedPublications.filter(
      publication => publication.__TYPE__ === 'news'
    );
  }, [suggestedPublications]);
  const popularNews = useMemo(() => {
    return popularPublications.items.filter(
      publication => publication.__TYPE__ === 'news'
    );
  }, [popularPublications]);

  const [featured, setFeatured] = useState(featuredPublications.items);
  const featuredNews = useMemo(() => {
    return featured.filter(
      publication => publication.__TYPE__ === 'news' && publication.country !== null
    );
  }, [featured]);
  const refreshFeatured = useCallback(async () => {
    setFeatured(await fetchFeaturedPublications(currentUser?.country?.id).then(result => result.items));
  }, [currentUser, setFeatured]);

  const [mobilePublish, setMobilePublish] = useState(false);
  const [tabIndex, setTabIndex] = useState(activeTab);
  const {
    isOpen: isOpenPublishModalOpen,
    onOpen: onPublishModalOpen,
    onClose: onPublishModalClose,
  } = useDisclosure();

  const {
    isOpen: isFilterModalOpen,
    onOpen: onFilterModalOpen,
    onClose: onFilterModalClose,
  } = useDisclosure();

  const {
    isOpen: isOpenPublishPedagogicalModalOpen,
    onOpen: onPublishPedagogicalModalOpen,
    onClose: onPublishPedagogicalModalClose,
  } = useDisclosure();
  const {
    isOpen: isLoginModalOpen,
    onOpen: onLoginModalOpen,
    onClose: onLoginModalClose,
  } = useDisclosure();

  return (
    <FrontLayout
      gtmPage={{
        page_type: 'Communauté',
        page_level1: 'Communautés',
        page_level2: 'Ma communauté',
        page_level3: 'Publications',
      }}
    >
      <Head>
        <title>Ma communauté | IFprofs</title>
      </Head>
      <Header
        mainNavItemActive={NavCategory.Communities}
        subNavItemActive={NavSubCategory.MY_COMMUNITY}
      />
      <Breadcrumb
        breadcrumbItems={[
          { label: 'Accueil', href: '/' },
          { label: 'Communautés', href: '/communautes' },
          { label: 'Ma communauté', href: '/communautes' },
          { label: 'Publications' },
        ]}
      >
        <BreadcrumbItem label="Accueil" href="/" />
        <BreadcrumbItem label="Communautés" href="/communautes" />
        <BreadcrumbItem label="Ma communauté" href="/communautes" />
        <BreadcrumbItem label="Publications" />
      </Breadcrumb>

      <UIWidth>
        <Chapo
          color="tournesol.100"
          heading="Ma communauté"
          icon={
            <CommunityPicto freeIconColor="currentColor" iconWidth="100%" />
          }
        >
          <Text fontSize={['1rem', '', '1.25rem']} pt="1rem">
            Retrouvez les publications de votre espace Pays (informations et
            ressources), d'IFprofs et de nos partenaires
          </Text>
        </Chapo>
      </UIWidth>
      <MobileBottomNav>
        {currentUser && (
          <>
            <Button
              variant="sunflower"
              sx={mobileBottomNavStyle}
              onClick={() => {
                setMobilePublish(true);
              }}
            >
              Publier
              <Icon as={PiNotePencilThin} boxSize="1rem" ml="0.625rem" />
            </Button>
            <Box width="1px" background="brand.100" />
          </>
        )}
        <Button
          variant="sunflower"
          sx={{
            ...mobileBottomNavStyle,
            width: currentUser ? 'calc(50% - 0.5px)' : '100%',
          }}
          onClick={onFilterModalOpen}
        >
          Trier / Filtrer
          <Icon as={PiShuffleThin} boxSize="1rem" ml="0.625rem" />
        </Button>
      </MobileBottomNav>
      <Box
        as="section"
        mt="2rem"
        backgroundImage={`url("${BgMasqueHaut.src}")`}
        backgroundRepeat="no-repeat"
        backgroundPosition={{ base: '0 -35rem', md: '0 0' }}
        backgroundSize="512rem auto"
        backgroundColor={{ md: 'tournesol.25' }}
      >
        <Box
          backgroundImage={{ sm: `url("${VolDeMouettesCommunautes.src}")` }}
          backgroundPosition={{ md: '0 18.5rem' }}
          backgroundSize="100% auto"
          backgroundRepeat="no-repeat"
          pb="2rem"
        >
          <UIWidth>
            <Tabs
              variant="unstyled"
              index={tabIndex}
              onChange={index => {
                if (index < 2) {
                  setTabIndex(index);
                }
              }}
            >
              <TabList
                sx={{
                  scrollbarWidth: 'none',
                  WebkitOverflowScrolling: 'touch',
                  '::-webkit-scrollbar': { display: 'none' },
                }}
              >
                <Tab>Toutes les publications</Tab>
                <Tab display={!currentUser ? 'none' : 'block'}>
                  {`L'actualité ${currentUser?.country?.name}`}
                </Tab>
                <Tab display={!currentUser ? 'none' : 'block'}>
                  <NextLink href="/communautes/ressources">
                    {`Les ressources ${currentUser?.country?.name}`}
                  </NextLink>
                </Tab>
                <Box position="absolute" right="0.625rem">
                  <HidableSearchBar
                    searchInit={search}
                    onSearchSubmit={newSearch => {
                      setSearch(newSearch);
                    }}
                  />
                </Box>
              </TabList>
              <TabIndicator
                display={['none', '', 'block']}
                mt="-5px"
                height="6px"
                bg="tournesol.100"
              />
              <TabPanels>
                <TabPanel pt={{ md: '3rem' }}>
                  {tabIndex === 0 && (
                    <MainPublicationsList
                      filter={filter}
                      setFilter={setFilter}
                      orders={allowedOrders}
                      order={order}
                      setOrder={setOrder}
                      search={search}
                      pinned={pinned}
                      setPinned={setPinned}
                      suggestedPublications={suggestedPublications}
                      popularPublications={popularPublications.items}
                      featuredPublications={featured}
                      refreshFeatured={refreshFeatured}
                      colleaguesOfColleagues={colleaguesOfColleagues}
                      recentGroupDiscussions={recentGroupDiscussions}
                      filterNews={false}
                      onPublishModalOpen={onPublishModalOpen}
                      onPublishPedagogicalModalOpen={
                        onPublishPedagogicalModalOpen
                      }
                      onClickLogin={onLoginModalOpen}
                    />
                  )}
                </TabPanel>

                <TabPanel pt={{ md: '3rem' }}>
                  {tabIndex === 1 && (
                    <MainPublicationsList
                      filter={filter}
                      setFilter={setFilter}
                      orders={allowedOrders}
                      order={order}
                      setOrder={setOrder}
                      search={search}
                      pinned={pinnedNews}
                      setPinned={setPinned}
                      suggestedPublications={suggestedNews}
                      popularPublications={popularNews}
                      featuredPublications={featuredNews}
                      refreshFeatured={refreshFeatured}
                      colleaguesOfColleagues={colleaguesOfColleagues}
                      recentGroupDiscussions={recentGroupDiscussions}
                      filterNews={true}
                      onPublishModalOpen={onPublishModalOpen}
                      onPublishPedagogicalModalOpen={
                        onPublishPedagogicalModalOpen
                      }
                      onClickLogin={onLoginModalOpen}
                    />
                  )}
                </TabPanel>
                <TabPanel pt={{ md: '3rem' }}></TabPanel>
              </TabPanels>
            </Tabs>
          </UIWidth>
        </Box>
      </Box>

      <PublishNewsModal
        isOpen={isOpenPublishModalOpen}
        onClose={onPublishModalClose}
        onSubmitEnd={(values, published) => {
          toast({
            title: published
              ? 'Information publiée'
              : 'Information enregistrée',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          onPublishModalClose();
        }}
      />
      <PublishPedagogicalResourceModal
        isOpen={isOpenPublishPedagogicalModalOpen}
        onClose={onPublishPedagogicalModalClose}
        onSubmitEnd={async (values, published) => {
          toast({
            title: published ? 'Ressource publiée' : 'Ressource enregistrée',
            status: 'success',
            duration: 9000,
            isClosable: true,
          });
          onPublishPedagogicalModalClose();
        }}
      />

      <SelectOnMobilePublishModal
        isOpen={mobilePublish}
        onClose={() => setMobilePublish(false)}
        onSubmitEnd={() => {
          toast({
            title: 'Ressource enregistrée',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          setMobilePublish(false);
        }}
      />
      {isLoginModalOpen && (
        <LoginModal
          onClose={onLoginModalClose}
          onLoginSuccess={user => {
            toast({
              title: `Bienvenue ${user?.displayName}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }}
        />
      )}

      {isFilterModalOpen && (
        <MobilePublicationTypeFilterModal
          filters={FILTERS}
          orders={allowedOrders}
          currentFilter={filter}
          currentOrder={order}
          onClose={onFilterModalClose}
          onSubmit={({ filter, order }) => {
            setFilter(filter);
            setOrder(order);
            onFilterModalClose();
          }}
        />
      )}
    </FrontLayout>
  );
}

Communities.getInitialProps = async (context: NextPageContextWithRedux) => {
  const currentUser = context.reduxStore.getState().authentication.user;

  return await Promise.all([
    fetchPinnedPublications(
      {
        worldOrCountry: currentUser?.country?.id ?? '',
      },
      { context }
    ),
    currentUser
      ? getCurrentUserSuggestions(true, false, false, { context })
      : [],
    fetchPopularPublications(
      ITEMS_PER_LOAD_MORE,
      1,
      {
        worldOrCountry: currentUser?.country?.id ?? '',
      },
      { context }
    ),
    fetchFeaturedPublications(currentUser?.country?.id, { context }),
    currentUser
      ? fetchColleaguesOfColleagues(currentUser, { context })
      : { items: [] },
    currentUser
      ? fetchRecentGroupDiscussions(currentUser.id, { context })
      : { items: [] },
  ]).then(data => {
    return {
      activeTab: context.query.activeTab
        ? parseInt(context.query.activeTab as string)
        : 0,
      pinnedPublications: data[0],
      suggestedPublications: data[1],
      popularPublications: data[2],
      featuredPublications: data[3],
      colleaguesOfColleagues: data[4],
      recentGroupDiscussions: data[5],
    };
  });
};

export default withUser(Communities);
